import React, { useState, useEffect } from "react";
import { format } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import { ro } from "date-fns/locale";
import { ChoghadiyaInterval } from "../utils/choghadiyaCalculator";
import CalendarSaveButton from "./CalendarSaveButton";

interface ChoghadiyaListProps {
  choghadiyaIntervals: ChoghadiyaInterval[];
  selectedTimezone?: string;
}

const ChoghadiyaList: React.FC<ChoghadiyaListProps> = ({
  choghadiyaIntervals,
  selectedTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone,
}) => {
  const [currentTime, setCurrentTime] = useState(new Date());

  // Update current time every second
  useEffect(() => {
    const intervalId = setInterval(() => {
      setCurrentTime(new Date());
    }, 10000);

    return () => clearInterval(intervalId);
  }, []);
  const formatTime = (date: Date) => {
    return formatInTimeZone(date, selectedTimezone, "HH:mm");
  };

  // Get date range for display
  const dateRangeStr =
    choghadiyaIntervals.length > 0
      ? `${format(choghadiyaIntervals[0].startTime, "d MMMM", { locale: ro })} - ${format(
          choghadiyaIntervals[choghadiyaIntervals.length - 1].endTime,
          "d MMMM",
          { locale: ro },
        )}`
      : "";

  // Separate day and night choghadiya
  const dayChoghadiya = choghadiyaIntervals.slice(0, 8);
  const nightChoghadiya = choghadiyaIntervals.slice(8, 16);

  // Helper function to get quality class
  const getQualityClass = (quality: string) => {
    switch (quality) {
      case "auspicious":
        return "auspicious";
      case "inauspicious":
        return "inauspicious";
      case "good":
        return "good";
      default:
        return "";
    }
  };

  // Helper function to get quality text
  const getQualityText = (quality: string) => {
    switch (quality) {
      case "auspicious":
        return "Favorabil";
      case "inauspicious":
        return "Nefavorabil";
      case "good":
        return "Bun";
      default:
        return "";
    }
  };

  // Check if a choghadiya is current
  const isCurrentChoghadiya = (choghadiya: ChoghadiyaInterval): boolean => {
    const now = currentTime.getTime();
    return (
      now >= choghadiya.startTime.getTime() &&
      now < choghadiya.endTime.getTime()
    );
  };

  return (
    <div className="muhurtas-list">
      <h3>Choghadiya ale zilei ({dateRangeStr})</h3>
      <p className="muhurta-info">
        Choghadiya reprezintă intervale de timp în astrologia vedică, fiecare
        avuând calități specifice și fiind guvernat de o planetă. Ziua și
        noaptea sunt împărțite în cuâte 8 intervale Choghadiya. Amrit, Labh,
        Shubh și Char sunt considerate favorabile. Amrit este considerată cea
        mai favorabilă choghadiya, iar Char este bună.
      </p>
      <div className="muhurtas-container">
        <div className="day-muhurtas">
          <h4>Ziuă (Răsărit - Apus) </h4>
          <ul>
            {dayChoghadiya.map((choghadiya, index) => (
              <li
                key={index}
                className={`muhurta-item ${getQualityClass(choghadiya.quality)} ${isCurrentChoghadiya(choghadiya) ? "current-interval" : ""}`}
              >
                <div className="muhurta-header">
                  <span className="muhurta-time">
                    {formatTime(choghadiya.startTime)} -{" "}
                    {formatTime(choghadiya.endTime)}
                  </span>
                  <span className="muhurta-name">
                    {choghadiya.romanianName}
                    <span className="muhurta-quality">
                      {` (${getQualityText(choghadiya.quality)})`}
                    </span>
                  </span>
                </div>
                <div className="muhurta-description">
                  <div>
                    <strong>Planetă:</strong> {choghadiya.rulingPlanet}
                  </div>
                  <div>{choghadiya.romanianDescription}</div>
                  <div className="muhurta-actions right-clandar-icon">
                    <CalendarSaveButton
                      event={{
                        title: `Choghadiya: ${choghadiya.romanianName}`,
                        description: `${choghadiya.romanianDescription}\nPlanetă: ${choghadiya.rulingPlanet}\nCalitate: ${getQualityText(choghadiya.quality)}`,
                        startTime: choghadiya.startTime,
                        endTime: choghadiya.endTime,
                      }}
                      buttonClass="right-calendar-icon"
                      showIcon={false}
                    >
                      <img
                        src="/images/apple-calendar-icon.png"
                        alt="Calendar"
                        width="16"
                        height="16"
                      />
                    </CalendarSaveButton>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
        <div className="night-muhurtas">
          <h4>Noapte (Apus - Răsărit) </h4>
          <ul>
            {nightChoghadiya.map((choghadiya, index) => (
              <li
                key={index}
                className={`muhurta-item ${getQualityClass(choghadiya.quality)} ${isCurrentChoghadiya(choghadiya) ? "current-interval" : ""}`}
              >
                <div className="muhurta-header">
                  <span className="muhurta-time">
                    {formatTime(choghadiya.startTime)} -{" "}
                    {formatTime(choghadiya.endTime)}
                  </span>
                  <span className="muhurta-name">
                    {choghadiya.romanianName}
                    <span className="muhurta-quality">
                      {` (${getQualityText(choghadiya.quality)})`}
                    </span>
                  </span>
                </div>
                <div className="muhurta-description">
                  <div>
                    <strong>Planetă:</strong> {choghadiya.rulingPlanet}
                  </div>
                  <div>{choghadiya.romanianDescription}</div>
                  <div className="muhurta-actions">
                    <CalendarSaveButton
                      event={{
                        title: `Choghadiya: ${choghadiya.romanianName}`,
                        description: `${choghadiya.romanianDescription}\nPlanetă: ${choghadiya.rulingPlanet}\nCalitate: ${getQualityText(choghadiya.quality)}`,
                        startTime: choghadiya.startTime,
                        endTime: choghadiya.endTime,
                      }}
                      buttonClass="right-calendar-icon"
                      showIcon={false}
                    >
                      <img
                        src="/images/apple-calendar-icon.png"
                        alt="Calendar"
                        width="16"
                        height="16"
                      />
                    </CalendarSaveButton>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ChoghadiyaList;
